import { createSignal } from 'solid-js'
import { trpc } from './trpc'

type MeResult = Awaited<ReturnType<typeof trpc.me.get.query>>

// Module-level singleton: every component that calls useMe() reads from the
// same signal, so route navigation doesn't refetch and there's no
// "Caricamento" flash on every page change.
//
// Boot hydration: the server emits a `<meta name="salun-initial-me">` tag
// in the SPA shell so the first paint can skip the `me.get` round-trip
// entirely. We read it once at module load and seed the signal; the meta
// tag is removed afterwards so any in-app session change goes through
// fetchMe / refreshMe rather than the stale boot value.
//
// `<meta>` is used rather than an inline `<script>` because the page CSP
// is `script-src 'self'` (no unsafe-inline / nonce).

function tenantSlugFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const m = /^\/t\/([^/]+)/.exec(window.location.pathname)
  return m ? m[1] : undefined
}

function readInitialMe(): MeResult | null {
  if (typeof document === 'undefined') return null
  const el = document.querySelector<HTMLMetaElement>(
    'meta[name="salun-initial-me"]',
  )
  if (!el) return null
  const raw = el.getAttribute('content')
  el.remove()
  if (!raw) return null
  try {
    return JSON.parse(raw) as MeResult
  } catch {
    return null
  }
}

const initial = readInitialMe()
const [meSignal, setMeSignal] = createSignal<MeResult | null>(initial)
let inflight: Promise<MeResult> | null = null
let lastSlug: string | undefined = initial ? tenantSlugFromUrl() : undefined

async function fetchMe(slug?: string): Promise<MeResult> {
  try {
    // Pass the URL's tenant slug so the server picks the right per-tab
    // session cookie (multi-tenant tab isolation).
    return await trpc.me.get.query(slug ? { tenantSlug: slug } : undefined)
  } catch {
    return { authenticated: false as const }
  }
}

function ensureLoaded(): void {
  const slug = tenantSlugFromUrl()
  if (meSignal() !== null && slug === lastSlug) return
  if (inflight) return
  lastSlug = slug
  inflight = fetchMe(slug).then((r) => {
    setMeSignal(r)
    inflight = null
    return r
  })
}

export function useMe() {
  ensureLoaded()
  return meSignal
}

/** Force-refetch (e.g. after login/logout).
 *  Pass `slugOverride` when the caller is about to navigate to a slug
 *  different from the current URL (e.g. /login → /t/<slug>/dashboard) so the
 *  signal is hydrated for the destination tenant, not the source page. */
export async function refreshMe(slugOverride?: string): Promise<void> {
  const slug = slugOverride ?? tenantSlugFromUrl()
  lastSlug = slug
  inflight = fetchMe(slug).then((r) => {
    setMeSignal(r)
    inflight = null
    return r
  })
  await inflight
}

/** Reset to "unknown" — call before redirecting to login. */
export function clearMe(): void {
  setMeSignal({ authenticated: false as const })
}
